import API from '@/utils/API';
import retry from 'async/retry';
import { ServiceUnavailableError } from '@/utils/API/core/exceptions';
import { cloudfrontURL } from '@/utils/url-builder';
import productSelectionParser from '@/utils/product-selection-parser';
import omit from 'lodash/omit';
import humps from 'humps';

/** Steps */
export const WELCOME_STEP = 'WELCOME_STEP';
export const PRODUCT_CHOICE_STEP = 'PRODUCT_CHOICE_STEP';
export const NEW_MORTGAGE_CONDITIONS_STEP = 'NEW_MORTGAGE_CONDITIONS_STEP';
export const BUY_MORTGAGE_CONDITIONS_STEP = 'BUY_MORTGAGE_CONDITIONS_STEP';
export const HOME_EQUITY_LOAN_CONDITIONS_STEP = 'HOME_EQUITY_LOAN_CONDITIONS_STEP';
export const MORE_INFO_STEP = 'MORE_INFO_STEP';
export const NO_PROPERTY_STEP = 'NO_PROPERTY_STEP';
export const LINK_SEND_STEP = 'LINK_SEND_STEP';
export const ERROR_STEP = 'ERROR_STEP';
export const NO_MORTGAGE_STEP = 'NO_MORTGAGE_STEP';
export const CUSTOMER_FORM_STEP = 'CUSTOMER_FORM_STEP';
export const PROPERTY_FORM_STEP = 'PROPERTY_FORM_STEP';
export const INCOMES_CATEGORY_STEP = 'INCOMES_CATEGORY_STEP';
export const INCOMES_FORM_STEP = 'INCOMES_FORM_STEP';
export const MORTGAGE_FORM_STEP = 'MORTGAGE_FORM_STEP';
export const HOW_OLD_BUY_PROPERTY_STEP = 'HOW_OLD_BUY_PROPERTY_STEP';
export const INDEPENDENT_MAYBE_STEP = 'INDEPENDENT_MAYBE_STEP';
export const NOT_INDEPENDENT_STEP = 'NOT_INDEPENDENT_STEP';
export const SUCCESS_STEP = 'SUCCESS_STEP';
export const NOT_SUNARP_STEP = 'NOT_SUNARP_STEP';
export const NO_INCOMES_SUPPORT_STEP = 'NO_INCOMES_SUPPORT_STEP';
export const NO_VALID_ADDRESS_STEP = 'NO_VALID_ADDRESS_STEP';
export const PROPERTY_ADDRESS_FORM_STEP = 'PROPERTY_ADDRESS_FORM_STEP';

/** Choices */
export const NEW_MORTGAGE_CHOICE = 'NEW_MORTGAGE_CHOICE';
export const BUY_MORTGAGE_CHOICE = 'BUY_MORTGAGE_CHOICE';
export const HOME_EQUITY_LOAN_CHOICE = 'HOME_EQUITY_LOAN_CHOICE';
export const CONFIRM_CHOICE = 'CONFIRM_CHOICE';
export const DECLINE_CHOICE = 'DECLINE_CHOICE';
export const NOT_SURE_CHOICE = 'NOT_SURE_CHOICE';
export const BLOG_CHOICE = 'BLOG_CHOICE';
export const EBOOKS_CHOICE = 'EBOOKS_CHOICE';
export const EBOOKS_STEP = 'EBOOKS_STEP';
export const BUY_SITUATION_STEP = 'BUY_SITUATION_STEP';
export const NOT_FOUND_YET_CHOICE = 'NOT_FOUND_YET_CHOICE';
export const HIGHER_CHOICE = 'HIGHER_CHOICE';
export const LOWER_CHOICE = 'LOWER_CHOICE';

/** Form Components **/
import CustomerFormComponent from './components/forms/customer-form';
import MortgageFormComponent from './components/forms/mortgage-form';
import PropertyFormComponent from './components/forms/property-form';
import PropertyAddressFormComponent from './components/forms/property-address-form';
import IncomesFormComponent from './components/forms/incomes-form';

/** Trust Banners */
// import TrustBanks from './components/trust/trust-banks';
import TrustMedios from './components/trust/trust-medios';
import TrustLoans from './components/trust/trust-loans';
import TrustAdvisory from './components/trust/trust-advisory';
import TrustFast from './components/trust/trust-fast';

import { MERGE_DATA } from '@/vuex/modules/flower';
import { userAccountURL, webSiteURL, blogURL } from '@/utils/url-builder';
import {
  NEW_MORTGAGE_SLUG,
  BUY_MORTGAGE_SLUG,
  HOME_EQUITY_LOAN_SLUG,
  ADVISER_SLUG,
  HOUSE_REMODELING_PRODUCT_MOTIVE,
  DEBT_CONSOLIDATION_PRODUCT_MOTIVE,
  WORK_CAPITAL_PRODUCT_MOTIVE,
} from '@/utils/constants';

const VUE_APP_DESCRIPTION_LONG = process.env.VUE_APP_DESCRIPTION_LONG || 'Prestamos con Garantia Hipotecaria en 10 dias.';
const redirectToAccount = store => {
  const data = store.state.data;

  const url = data?.loginURL || userAccountURL(
    '/login',
    {
      queryParams: {
        email: data?.customer?.email,
        dni: data?.customer?.dni,
      },
    },
  );

  window.location.href = url;
};

const nextStepFromProductSelection = product => {
  switch (product) {
    case NEW_MORTGAGE_SLUG:
      return NEW_MORTGAGE_CONDITIONS_STEP;
    case BUY_MORTGAGE_SLUG:
      return BUY_MORTGAGE_CONDITIONS_STEP;
    case HOME_EQUITY_LOAN_SLUG:
      return HOME_EQUITY_LOAN_CONDITIONS_STEP;
    case ADVISER_SLUG:
      return CUSTOMER_FORM_STEP;
    default:
      return false;
  }
};

const VALID_PROVINCE_IDS = [
  '153', // LIMA
  '61', // AREQUIPA
  '93', // CUSCO
  '218', // CALLAO
  '138', // TRUJILLO
  '150', // CHICLAYO
];

const steps = {
  /**
   * Debido a que es mas dificil trabajar con objetos como valor debido a la reactividad de VUE, convertimos
   * los valores a un string que incluye el producto y el motivo y de esta manera es mas facil manejarlos
   */
  [WELCOME_STEP]: {
    question: {
      title: '¡Hola!, Empecemos a simular el préstamo con garantía hipotecaria que necesitas',
      description: VUE_APP_DESCRIPTION_LONG,
    },
    choice({ store }) {
      return {
        component: 'product-choice',
        default: { value: productSelectionParser.stringify(store.state.data.product, store.state.data.motive) },
        items: [
          {
            title: 'Soy empresario y <b>necesito capital para mi negocio</b>',
            value: productSelectionParser.stringify(HOME_EQUITY_LOAN_SLUG, WORK_CAPITAL_PRODUCT_MOTIVE),
          },
          {
            title: 'Quiero <b>consolidar mis deudas</b>',
            value: productSelectionParser.stringify(HOME_EQUITY_LOAN_SLUG, DEBT_CONSOLIDATION_PRODUCT_MOTIVE),
          },
          {
            title: 'Necesito <b>capital para construcción</b>',
            value: productSelectionParser.stringify(HOME_EQUITY_LOAN_SLUG, HOUSE_REMODELING_PRODUCT_MOTIVE),
          },
          {
            title: '<b>Libre disponibilidad</b> con garantía hipotecaria',
            value: productSelectionParser.stringify(HOME_EQUITY_LOAN_SLUG),
          }
        ],
      };
    },
    mounted({ store }) {
      const product = store.state.data?.product;

      store.dispatch('trust/setBanner', TrustMedios, { root: true });

      if (!store.state.data?.product) return;

      const stepName = nextStepFromProductSelection(product)

      if (stepName)
        store.dispatch('goToStep', {
          step: getStepByName(nextStepFromProductSelection(product)),
        });
    },
    beforeNext({ store }) {
      const { product, motive } = productSelectionParser.parse(store.getters.lastAnswer.value);

      store.commit(MERGE_DATA, { product, motive });
    },
    nextStep: ({ store: { getters: { lastAnswer: { value } } } }) => {
      const { product } = productSelectionParser.parse(value);

      return nextStepFromProductSelection(product);
    },
  },

  [NEW_MORTGAGE_CONDITIONS_STEP]: {
    progress: 5,
    question: {
      title: `Por el momento no estamos trabajando créditos hipotecarios.`,
      description: `Aquí puedes conocer la diferencia: <a href="https://rebajatuscuentas.com/pe/ayuda/diferencia-entre-credito-hipotecario-y-prestamo-con-garantia-hipotecaria/">Diferencia entre crédito hipotecario y préstamo con garantía hipotecaria</a>`
    },
  },

  [BUY_MORTGAGE_CONDITIONS_STEP]: {
    progress: 5,
    question: {
      title: `Por el momento no estamos trabajando créditos hipotecarios.`,
      description: `Aquí puedes conocer la diferencia: <a href="https://rebajatuscuentas.com/pe/ayuda/diferencia-entre-credito-hipotecario-y-prestamo-con-garantia-hipotecaria/">Diferencia entre crédito hipotecario y préstamo con garantía hipotecaria</a>`
    },
  },

  [HOME_EQUITY_LOAN_CONDITIONS_STEP]: {
    progress: 5,
    question: {
      title: '¿Tu inmueble está inscrito en SUNARP?',
      description: 'Un inmueble inscrito en Registros Públicos tiene <a href="https://rebajatuscuentas.com/pe/blog/la-partida-registral-de-un-inmueble" target="_blank">partida registral</a>',
    },
    choice: {
      items: [
        { title: 'Si, está inscrito', value: CONFIRM_CHOICE },
        { title: 'No, no está inscrito', value: DECLINE_CHOICE },
        {
          title: 'No lo sé',
          value: NOT_SURE_CHOICE,
          help: '<a href="https://rebajatuscuentas.com/pe/blog/la-partida-registral-de-un-inmueble" target="_blank">Partida registral</a> de un inmueble <br></br>' +
            'Es el documento en el cual se asienta ante registros públicos un bien inmueble y se le da vida legal al mismo, para más información puedes leer <a href="https://rebajatuscuentas.com/pe/blog/la-partida-registral-de-un-inmueble" target="_blank">este articulo</a>',
        },
      ],
    },
    nextStep({ store: { getters: { lastAnswer: { value } } } }) {
      switch (value) {
        case CONFIRM_CHOICE:
          return INCOMES_CATEGORY_STEP;
        case DECLINE_CHOICE:
          return NOT_SUNARP_STEP;
        default:
          return false;
      }
    },
  },

  get [NO_INCOMES_SUPPORT_STEP]() {
    return {
      ...this[MORE_INFO_STEP],
      question: {
        title: 'Si no tienes cómo sustentar ingresos, no vamos a poder ayudarte por el momento 😢',
        description: '¿Cómo te gustaríá continuar?',
      },
    };
  },

  [NOT_SUNARP_STEP]: {
    question: {
      title: 'No podemos ayudarte 😢',
      description: 'Es necesario que tu inmueble esté correctamente inscrito en registros públicos para poder hipotecarlo. Puedes leer más información <a href="https://rebajatuscuentas.com/pe/blog/por-que-es-importante-el-registro-de-inmueble">aquí</a>.',
    },
    nextButtonText: 'Más información',
    nextStep: MORE_INFO_STEP,
  },

  [HOW_OLD_BUY_PROPERTY_STEP]: {
    progress: 10,
    question: {
      title: '¿Hace cuanto tiempo compraste el inmueble?',
    },
    choice: {
      items: [
        { title: 'Hace más de 6 meses', value: HIGHER_CHOICE },
        { title: 'Hace menos de 6 meses', value: LOWER_CHOICE },
      ],
    },
    nextStep({ store: { getters: { lastAnswer: { value } } } }) {
      switch (value) {
        case HIGHER_CHOICE:
          return INDEPENDENT_MAYBE_STEP;
        case LOWER_CHOICE:
          return NOT_INDEPENDENT_STEP;
      }
    },
  },

  [INDEPENDENT_MAYBE_STEP]: {
    progress: 15,
    question: {
      title: 'No te preocupes',
      description: 'Lo mas probable es que ya esté independizado, Igual te recomendamos que lo revises con la inmobiliaria',
    },
    nextStep: INCOMES_CATEGORY_STEP,
  },

  [MORE_INFO_STEP]: {
    question: {
      title: '¿Te gustaría que te ayudemos en algo más?',
    },
    choice: {
      items: [
        { title: 'No, eso es todo', value: DECLINE_CHOICE, href: webSiteURL() },
        { title: 'Leer nuestro blog', value: BLOG_CHOICE, href: blogURL() },
        { title: 'Descargar ebooks', value: EBOOKS_CHOICE, href: webSiteURL('/ebooks-financieros-gratuitos') },
      ],
    },
  },

  [NO_VALID_ADDRESS_STEP]: {
    question: {
      title: 'Lo siento, por ahora no podemos atender la provincia donde se encuentra el inmueble',
      description: '¿Te gustaría que te ayudemos en algo más?',
    },
    choice: {
      items: [
        { title: 'No, eso es todo', value: DECLINE_CHOICE, href: webSiteURL() },
        { title: 'Leer nuestro blog', value: BLOG_CHOICE, href: blogURL() },
        { title: 'Descargar ebooks', value: EBOOKS_CHOICE, href: webSiteURL('/ebooks-financieros-gratuitos') },
      ],
    },
  },

  [NO_PROPERTY_STEP]: {
    question: {
      title: 'Excelente',
      description: 'Si todavía no encuentras el inmueble te recomendamos algunas de estas opciones.',
    },
    choice: {
      items: [
        { title: 'Guía de Créditos Hipotecarios', href: webSiteURL('/guia-de-creditos-hipotecarios') },
        { title: 'Se un experto en Créditos Hipotecarios', href: blogURL('/category/creditos-hipotecarios') },
      ],
    },
  },

  [NOT_INDEPENDENT_STEP]: {
    progress: 80,
    question: {
      title: 'Quizás no esté independizado',
      description: 'Hay una probabilidad alta de que tu inmueble no esté independizado y con la hipoteca matriz levantada.</br>' +
        '<b class="has-text-primary">Comunícate con la inmobiliaria que te vendió el inmueble y confirma este dato</b><br/></br>' +
        `Aquí puedes ver mas informacion: <a href="${blogURL('/5-evidencias-identificar-inmueble-esta-independizado')}">${blogURL('/5-evidencias-identificar-inmueble-esta-independizado')}</a>`
    },
    nextStep: MORE_INFO_STEP,
  },

  [LINK_SEND_STEP]: {
    question: {
      title: 'Tu enlace ha sido enviado',
      description: 'Te acabamos de enviar un enlace a tu correo, con el vas a poder ingresar',
    },
    mounted({ store: { state: { data: { customer } } } }) {
      API.token({ email: customer.email });
    },
  },

  [ERROR_STEP]: {
    question: {
      title: 'Ha ocurrido un error',
      description: 'Por favor comunícate con nosotros para poder ayudarte al <a href="tel:+51-1-709-7730">1-709-7730</a> o al correo <a href="mailto:ayuda@rebajatuscuentas.com">ayuda@rebajatuscuentas.com</a>.'
    },
  },

  [NO_MORTGAGE_STEP]: {
    question: {
      title: 'No tienes una hipoteca',
      description: 'Con el DNI que nos proporcionaste no se puede realizar una compra de hipoteca',
    },
    choice: {
      items: [
        { title: 'Elegir otro producto', value: CONFIRM_CHOICE },
        { title: 'Ir al Blog', href: blogURL() },
      ],
    },
    beforeNext({ store }) {
      const product = store.getters.lastAnswer.value;

      store.commit(MERGE_DATA, { product });
    },
    nextStep({ store: { getters: { lastAnswer: { value } } } }) {
      switch (value) {
        case CONFIRM_CHOICE:
          return WELCOME_STEP;
      }
    },
  },

  [CUSTOMER_FORM_STEP]: {
    progress: 80,
    question({ store }) {
      const product = store.state.data.product;

      return {
        title: 'Crea una cuenta con nosotros',
        description: do {
          if(product === ADVISER_SLUG) 'Al crear tu cuenta, un asesor se pondrá en contacto contigo';
        else 'En tu cuenta vas a poder hacer seguimiento a tu crédito ver si calificas y las propuestas que tenemos para ti.'
      },
      }
  },
  form: {
    component: CustomerFormComponent,
    dataKey: 'customer',
  },
  mounted({ store }) {
    store.dispatch('trust/setBanner', TrustFast, { root: true });
  },
  async beforeNext({ store }) {
    const data = store.state.data;
    const { commit } = store;

    const formData = store.getters.lastAnswer;
    const propertyFormData = data.property;
    const mortgageFormData = data.mortgage || {};
    const incomesFormData = data.income;
    const propertyTypeFormData = data.propertyType || {};
    const propertyAddressFormData = data.propertyAddress?.address ? omit(data.propertyAddress.address, ['_locationIds']) : undefined;
    const product = data.product;
    const motive = data.motive;
    const adviser = store.rootGetters['adviser/adviser'];

    store.dispatch('removeNotification');

    try {
      const searchParams = new URLSearchParams(window.location.search);

      const requestData = product !== ADVISER_SLUG
        ? {
          adviserId: adviser.real_adviser_id,
          email: formData.email,
          product,
          motive,
          vat: formData.vat,
          vatType: formData.vatType,
          mobile: formData.phone,
          source: searchParams.get('utm_source'),
          medium: searchParams.get('utm_medium'),
          campaign: searchParams.get('utm_campaign'),
          referrerId: searchParams.get('referrer_id'),
          // --- Property
          property: {
            ...propertyFormData,
            ...propertyTypeFormData,
            address: propertyAddressFormData,
          },
          // --- Credit
          ...mortgageFormData ? {
            credit: { ...mortgageFormData },
          } : {},
          // --- Incomes
          incomes: [
            { ...incomesFormData },
          ],
          create_deal: true,
          show_report: false,

        } : {
          adviserId: adviser.real_adviser_id,
          email: formData.email,
          product,
          vat: formData.vat,
          vatType: formData.vatType,
          mobile: formData.phone,
          source: searchParams.get('utm_source'),
          medium: searchParams.get('utm_medium'),
          campaign: searchParams.get('utm_campaign'),
          referrerId: searchParams.get('referrer_id'),
          create_deal: false,
          show_report: false,
        };

      return await retry(
        {
          times: 5,
          interval: 2000,
          errorFilter(err) {
            return err instanceof ServiceUnavailableError;
          },
        },
        (cb) => {
          API.form(humps.decamelizeKeys(requestData))
            .then(({ data: responseData, status }) => {
              if (status === 200) {
                if (window.dataLayer) {
                  window.dataLayer.push({
                    event: 'Win_Customer',
                    email: requestData.email,
                  });
                }
                commit(MERGE_DATA, {
                  token: responseData.token,
                  loginURL: responseData.loginUrl,
                  firstName: responseData.firstName,
                  lastName: responseData.lastName,
                  propertyValue: parseInt(responseData?.propertyValue) || 0,
                });

                API.headers({ Authorization: `JWT ${responseData.token}` });

                cb(null, undefined);
              } else if (status === 202) {
                store.dispatch('addNotification', { type: 'danger', body: `Al parecer ya tienes una cuenta con nosotros. <a href="${userAccountURL('/login')}">Haz click aquí</a> para ingresar a tu cuenta y continuar con tu solicitud.` });

                return cb(null, false);
              } else if (status === 209) {
                store.dispatch('addNotification', { type: 'danger', body: `El DNI que nos proporcionaste no tiene una hipoteca inscrita según la información que nos proporciona las centrales de riesgos. Comunícate con nosotros para continuar al <a href="tel:+51-1-709-7730">1-709-7730</a> o al correo <a href="mailto:ayuda@rebajatuscuentas.com">ayuda@rebajatuscuentas.com</a>.` });

                return cb(null, false);
              } else if (status === 503) {
                return new ServiceUnavailableError();
              } else {
                return new Error('Ha ocurrido un error desconocido');
              }
            })
            .catch((error) => cb(error));
        },
      );
    } catch (error) {
      return ERROR_STEP;
    }
  },
  nextButtonText: 'Crear una cuenta y ver simulación de préstamo',
  nextStep: SUCCESS_STEP,
},

  [PROPERTY_FORM_STEP]: {
    progress: 60,
    mounted({ store }) {
      store.dispatch('trust/setBanner', TrustAdvisory, { root: true });
    },
question({ store }) {
  const product = store.state.data.product;

  return {
    title: do{
      if(product === NEW_MORTGAGE_SLUG) '¿Cuánto vale la propiedad que quieres comprar?';
    else if(product === BUY_MORTGAGE_SLUG) '¿Cuánto vale la propiedad asociado al crédito hipotecario?';
          else if (product === HOME_EQUITY_LOAN_SLUG) '¿Cuánto vale la propiedad que quieres dejar en garantía?';
  else '¿Cual es el valor de la propiedad?';
},
      };
    },
form: {
  component: PropertyFormComponent,
    dataKey: 'property',
    },
nextStep({ store }) {
  const product = store.state.data.product;

  if (product === BUY_MORTGAGE_SLUG) return MORTGAGE_FORM_STEP;
  else if (product === HOME_EQUITY_LOAN_SLUG) return PROPERTY_ADDRESS_FORM_STEP;
  else return CUSTOMER_FORM_STEP;
},
  },

[PROPERTY_ADDRESS_FORM_STEP]: {
  progress: 65,
    question: {
    title: '¿Cual es la dirección de la propiedad a hipotecar?',
      description: 'Necesitamos conocer la dirección de la propiedad',
    },
  form: {
    component: PropertyAddressFormComponent,
      dataKey: 'propertyAddress',
    },
  nextStep({ store: { getters: { lastAnswer } } }) {
    const provinceId = lastAnswer.address._locationIds[2];

    return VALID_PROVINCE_IDS.includes(String(provinceId)) ? CUSTOMER_FORM_STEP : NO_VALID_ADDRESS_STEP;
  },
},

[MORTGAGE_FORM_STEP]: {
  progress: 70,
    question: {
    title: 'Información sobre el crédito actual',
      description: 'Necesitamos información lo más precisa de tu crédito actual. Esto nos permitirá mostrar propuestas de ahorro.',
    },
  form: {
    component: MortgageFormComponent,
      dataKey: 'mortgage',
    },
  nextStep: CUSTOMER_FORM_STEP,
  },

[INCOMES_CATEGORY_STEP]: {
  progress: 50,
    mounted({ store }) {
    store.dispatch('trust/setBanner', TrustLoans, { root: true });
  },
  question: {
    title: '¿Cómo percibes tus ingresos?',
      description: 'Coloca la categoría principal de cómo percibes tus ingresos.',
    },
  choice: {
    items: [
      { title: '5ta', text: 'Planilla', value: 5, icon: cloudfrontURL('/icons/income-categories/employee.svg') },
      { title: '4ta', text: 'Trabajador Independiente, Recibo por Honorarios.', value: 4, icon: cloudfrontURL('/icons/income-categories/services.svg') },
      { title: 'No declarados', text: 'No declarados, sin sustento', value: 7, icon: cloudfrontURL('/icons/income-categories/other.svg') },
      { title: '1ra', text: 'Alquiler de bienes e inmuebles', value: 1, icon: cloudfrontURL('/icons/income-categories/rent.svg') },
      { title: '3ra', text: 'Actividades empresariales', value: 3, icon: cloudfrontURL('/icons/income-categories/business.svg') },
    ],
      scrollOnSelect: true,
    },
  nextStep: INCOMES_FORM_STEP,
  },

[INCOMES_FORM_STEP]: {
  progress: 51,
    question: {
    title: 'Necesitamos conocer tus ingresos',
      description: 'Coloca el total de ingresos brutos. Tus datos son protegidos y cifrados 🔒',
    },
  form: {
    component: IncomesFormComponent,
      dataKey: 'income',
    },
  nextStep: PROPERTY_FORM_STEP,
  },

[SUCCESS_STEP]: {
  progress: 100,
    question({ store }) {
    const product = store.state.data.product;

    return {
      title: '¡Felicidades!',
      description: do {
        if(product === ADVISER_SLUG) 'Estamos notificando a tu asesor. <br><br> Serás redireccionado en 3 segundos';
      else 'Ahora vamos a pre-calificarte y mostrarte propuestas de crédito para tu solicitud. <br><br> Serás redireccionado en 3 segundos'
    },
      }
},

mounted({ store }) {
  setTimeout(() => {
    redirectToAccount(store);
  }, 4000);
},
  },
};

export const getStepByName = name => steps[name];

export default steps;

